import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Separator } from '@shared/components/ui/separator';
import { Card, CardContent } from '@shared/components/ui/card';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@shared/components/ui/form';
import { AlertCircle, CreditCard, Shield, Zap } from 'lucide-react';
import { useAuthStore } from '@features/auth/hooks/useAuthStore';
import { getPartnerConfigWithFallback, setCurrentPartner } from '@shared/utils/partner.utils';
import { DevRoleSelector } from './DevRoleSelector';
import { SocialAuthButtons } from './SocialAuthButtons';
import { loginSchema, type LoginFormValues } from './auth-schemas';

interface PartnerLoginPageProps {
  partnerParam: string;
}

export function PartnerLoginPage({ partnerParam }: PartnerLoginPageProps) {
  const navigate = useNavigate();
  const { login, socialLogin, error, clearError } = useAuthStore();

  // Get partner configuration using the utility
  const partnerConfig = getPartnerConfigWithFallback(partnerParam);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const { isSubmitting } = form.formState;

  const onSubmit = async (values: LoginFormValues) => {
    clearError();
    try {
      await login(values.email, values.password);
      const state = useAuthStore.getState();
      if (state.pendingOtp) {
        toast.info('Enter your authenticator code to continue');
        return;
      }
      if (state.isAuthenticated) {
        setCurrentPartner(partnerParam);
        toast.success('Signed in successfully');
        navigate('/', { replace: true });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to sign in');
    }
  };

  const handleSocial = async (provider: 'google' | 'passkey') => {
    clearError();
    try {
      await socialLogin(provider);
      if (useAuthStore.getState().isAuthenticated) {
        setCurrentPartner(partnerParam);
        toast.success('Signed in successfully');
        navigate('/', { replace: true });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to sign in');
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="w-1/2 relative overflow-hidden"
        style={{ backgroundColor: partnerConfig.primaryColor }}
      >
        <div className="relative z-10 flex flex-col justify-between p-12 text-white h-full">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl transform translate-x-32 -translate-y-32" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl transform -translate-x-16 translate-y-16" />

          <div className="relative z-10">
            {/* Partner logos */}
            <div className="flex items-center gap-4 mb-16">
              <img src="/images/vinr-light.svg" alt="Vinr" className="h-8" />
              <span className="text-2xl font-normal opacity-60">×</span>
              <img 
                src={partnerConfig.logoPath} 
                alt={partnerConfig.displayName} 
                className="h-8"
                onError={(e) => {
                  // Fallback to text if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const textSpan = document.createElement('span');
                  textSpan.className = 'text-2xl font-semibold';
                  textSpan.textContent = partnerConfig.displayName;
                  target.parentNode?.appendChild(textSpan);
                }}
              />
            </div>

            <div className="max-w-lg">
              {/* Features */}
              <div className="space-y-6 mb-16">
                <div className="flex gap-4 group">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-colors">
                    <CreditCard className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 text-lg">Unified Payments</h3>
                    <p className="text-sm opacity-80 leading-relaxed">
                      Seamlessly integrate billing with your account
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 group">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-colors">
                    <Shield className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 text-lg">Bank-Level Security</h3>
                    <p className="text-sm opacity-80 leading-relaxed">
                      Your data is protected by enterprise security
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 group">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-colors">
                    <Zap className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 text-lg">Instant Settlements</h3>
                    <p className="text-sm opacity-80 leading-relaxed">Get paid faster with modern payment rails</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust message */}
          <div className="relative z-10">
            <p className="text-sm opacity-80 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Trusted by thousands of businesses worldwide
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-8 bg-muted">
        <div className="w-full max-w-md">
          <Card className="border-0 shadow-xl bg-white rounded-2xl">
            <CardContent className="p-8 rounded-2xl">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-2">Partner Sign In</h2>
              </div>

              <DevRoleSelector />

              {error && (
                <Alert variant="destructive" icon={AlertCircle} className="mb-6">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  noValidate
                  className="flex flex-col gap-5"
                >
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            autoComplete="email"
                            placeholder="you@company.com"
                            className="h-11"
                            disabled={isSubmitting}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            autoComplete="current-password"
                            className="h-11"
                            disabled={isSubmitting}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="h-11 w-full font-medium transition-opacity duration-200 hover:opacity-90"
                    style={{
                      backgroundColor: partnerConfig.primaryColor,
                      color: partnerConfig.secondaryColor,
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Signing in...' : 'Sign in'}
                  </Button>
                </form>
              </Form>

              {/* Divider */}
              <div className="my-6 flex items-center gap-4">
                <Separator className="flex-1" />
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Or continue with
                </span>
                <Separator className="flex-1" />
              </div>

              <SocialAuthButtons
                onGoogle={() => handleSocial('google')}
                onPasskey={() => handleSocial('passkey')}
                disabled={isSubmitting}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
